/**
 * Payment Settings Page
 *
 * Configure payment gateways: Stripe, PayPal, Tranzilla, HYP, and more.
 */

import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { api } from '../../lib/api';

interface Store {
  id: string;
  name: string;
  currency: string;
}

interface OutletContext {
  store: Store;
}

interface PaymentGateway {
  id: string;
  gateway_type: string;
  display_name: string;
  is_enabled: boolean;
  is_test_mode: boolean;
  credentials: Record<string, string>;
  settings: Record<string, unknown>;
  supported_currencies: string[];
}

interface GatewayConfig {
  type: string;
  name: string;
  description: string;
  icon: string;
  fields: GatewayField[];
  testModeSupported: boolean;
  regions: string[];
}

interface GatewayField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'select';
  placeholder?: string;
  required: boolean;
  options?: { value: string; label: string }[];
  testKey?: string; // For test mode credentials
}

const GATEWAY_CONFIGS: GatewayConfig[] = [
  {
    type: 'stripe',
    name: 'Stripe',
    description: 'Accept credit cards, Apple Pay, Google Pay, and more',
    icon: '💳',
    testModeSupported: true,
    regions: ['global'],
    fields: [
      { key: 'publishable_key', label: 'Publishable Key', type: 'text', placeholder: 'pk_live_...', required: true, testKey: 'pk_test_...' },
      { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'sk_live_...', required: true, testKey: 'sk_test_...' },
      { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'whsec_...', required: false },
    ],
  },
  {
    type: 'paypal',
    name: 'PayPal',
    description: 'Accept PayPal payments and credit cards',
    icon: '🅿️',
    testModeSupported: true,
    regions: ['global'],
    fields: [
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Your PayPal Client ID', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Your PayPal Client Secret', required: true },
    ],
  },
  {
    type: 'tranzilla',
    name: 'Tranzilla',
    description: 'Israeli payment gateway for credit card processing',
    icon: '🇮🇱',
    testModeSupported: true,
    regions: ['IL'],
    fields: [
      { key: 'terminal_name', label: 'Terminal Name', type: 'text', placeholder: 'Your terminal name', required: true },
      { key: 'terminal_password', label: 'Terminal Password', type: 'password', required: true },
      { key: 'supplier_code', label: 'Supplier Code', type: 'text', required: false },
    ],
  },
  {
    type: 'hyp',
    name: 'HYP (Yaad Pay)',
    description: 'Israeli payment gateway with local payment methods',
    icon: '🇮🇱',
    testModeSupported: true,
    regions: ['IL'],
    fields: [
      { key: 'terminal_id', label: 'Terminal ID (Masof)', type: 'text', required: true },
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'pass_p', label: 'PassP', type: 'password', required: true },
    ],
  },
  {
    type: 'cod',
    name: 'Cash on Delivery',
    description: 'Collect payment when order is delivered',
    icon: '💵',
    testModeSupported: false,
    regions: ['global'],
    fields: [
      { key: 'instructions', label: 'Payment Instructions', type: 'text', placeholder: 'Instructions shown to customer', required: false },
    ],
  },
  {
    type: 'manual',
    name: 'Manual Payment',
    description: 'Bank transfer, check, or other offline payment',
    icon: '🏦',
    testModeSupported: false,
    regions: ['global'],
    fields: [
      { key: 'instructions', label: 'Payment Instructions', type: 'text', placeholder: 'Bank details or payment instructions', required: true },
    ],
  },
];

export default function PaymentsSettingsPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;
  const effectiveStoreId = store?.id || storeId || '';

  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedGateway, setExpandedGateway] = useState<string | null>(null);
  const [editingCredentials, setEditingCredentials] = useState<Record<string, Record<string, string>>>({});

  const loadGateways = useCallback(async () => {
    if (!effectiveStoreId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/ecommerce/stores/${effectiveStoreId}/payment-gateways`);
      const data = await response.json() as { gateways: PaymentGateway[] };
      setGateways(data.gateways || []);

      // Initialize editing credentials
      const creds: Record<string, Record<string, string>> = {};
      (data.gateways || []).forEach((gw: PaymentGateway) => {
        creds[gw.gateway_type] = { ...gw.credentials };
      });
      setEditingCredentials(creds);
    } catch {
      // No gateways configured yet
      setGateways([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveStoreId]);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  const getGatewayConfig = (type: string) => {
    return GATEWAY_CONFIGS.find(g => g.type === type);
  };

  const getExistingGateway = (type: string) => {
    return gateways.find(g => g.gateway_type === type);
  };

  const handleCredentialChange = (gatewayType: string, key: string, value: string) => {
    setEditingCredentials(prev => ({
      ...prev,
      [gatewayType]: {
        ...prev[gatewayType],
        [key]: value,
      },
    }));
  };

  const handleSaveGateway = async (gatewayType: string) => {
    if (!effectiveStoreId) return;

    const config = getGatewayConfig(gatewayType);
    if (!config) return;

    const existing = getExistingGateway(gatewayType);
    const credentials = editingCredentials[gatewayType] || {};

    // Validate required fields
    for (const field of config.fields) {
      if (field.required && !credentials[field.key]) {
        setError(`${field.label} is required for ${config.name}`);
        return;
      }
    }

    try {
      setSaving(gatewayType);
      setError(null);

      const payload = {
        gateway_type: gatewayType,
        display_name: config.name,
        is_enabled: existing?.is_enabled ?? false,
        is_test_mode: existing?.is_test_mode ?? true,
        credentials,
        settings: existing?.settings || {},
        supported_currencies: [store?.currency || 'USD'],
      };

      if (existing) {
        await api.put(`/api/ecommerce/stores/${effectiveStoreId}/payment-gateways/${gatewayType}`, payload);
      } else {
        await api.post(`/api/ecommerce/stores/${effectiveStoreId}/payment-gateways`, payload);
      }

      setSuccess(`${config.name} settings saved successfully`);
      setTimeout(() => setSuccess(null), 3000);
      loadGateways();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save gateway settings');
    } finally {
      setSaving(null);
    }
  };

  const handleToggleEnabled = async (gatewayType: string, enabled: boolean) => {
    if (!effectiveStoreId) return;

    const existing = getExistingGateway(gatewayType);
    if (!existing) {
      setError('Please configure the gateway settings first');
      return;
    }

    try {
      setSaving(gatewayType);
      await api.put(`/api/ecommerce/stores/${effectiveStoreId}/payment-gateways/${gatewayType}`, {
        ...existing,
        is_enabled: enabled,
      });
      loadGateways();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update gateway');
    } finally {
      setSaving(null);
    }
  };

  const handleToggleTestMode = async (gatewayType: string, testMode: boolean) => {
    if (!effectiveStoreId) return;

    const existing = getExistingGateway(gatewayType);
    if (!existing) return;

    try {
      setSaving(gatewayType);
      await api.put(`/api/ecommerce/stores/${effectiveStoreId}/payment-gateways/${gatewayType}`, {
        ...existing,
        is_test_mode: testMode,
      });
      loadGateways();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update gateway');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Payment Methods</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Configure payment gateways to accept payments from customers
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
        </div>
      )}

      <div className="space-y-4">
        {GATEWAY_CONFIGS.map(config => {
          const existing = getExistingGateway(config.type);
          const isExpanded = expandedGateway === config.type;
          const credentials = editingCredentials[config.type] || {};

          return (
            <div
              key={config.type}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Gateway Header */}
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => setExpandedGateway(isExpanded ? null : config.type)}
              >
                <div className="flex items-center gap-4">
                  <span className="text-2xl">{config.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{config.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{config.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {existing && (
                    <div className="flex items-center gap-2">
                      {existing.is_test_mode && (
                        <span className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded">
                          Test Mode
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleEnabled(config.type, !existing.is_enabled);
                        }}
                        disabled={saving === config.type}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          existing.is_enabled ? 'bg-teal-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                            existing.is_enabled ? 'translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Gateway Configuration */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                  {/* Test Mode Toggle */}
                  {config.testModeSupported && existing && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">Test Mode</div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Use sandbox/test credentials for testing
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleTestMode(config.type, !existing.is_test_mode)}
                        disabled={saving === config.type}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          existing.is_test_mode ? 'bg-amber-500' : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                            existing.is_test_mode ? 'translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>
                  )}

                  {/* Credential Fields */}
                  {config.fields.map(field => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {field.label} {field.required && '*'}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          value={credentials[field.key] || ''}
                          onChange={(e) => handleCredentialChange(config.type, field.key, e.target.value)}
                          className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                        >
                          <option value="">Select...</option>
                          {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          value={credentials[field.key] || ''}
                          onChange={(e) => handleCredentialChange(config.type, field.key, e.target.value)}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400"
                        />
                      )}
                    </div>
                  ))}

                  {/* Save Button */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => handleSaveGateway(config.type)}
                      disabled={saving === config.type}
                      className="px-6 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                    >
                      {saving === config.type ? 'Saving...' : existing ? 'Update Settings' : 'Configure Gateway'}
                    </button>
                  </div>

                  {/* Help Text */}
                  {config.type === 'stripe' && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Get your API keys from the{' '}
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline">
                          Stripe Dashboard
                        </a>
                      </p>
                    </div>
                  )}
                  {config.type === 'paypal' && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        Get your credentials from the{' '}
                        <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" className="underline">
                          PayPal Developer Dashboard
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Need a different payment method?</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Contact support to request additional payment gateway integrations. We can add support for local payment methods in your region.
        </p>
      </div>
    </div>
  );
}
